import { staticFile } from 'remotion';

// Types
import type { EmployeeTraining } from '@/types';
import type {
  Scene5bCardData,
  Scene5bCardGroup,
  Scene5bCardGroupWithTiming,
} from '../types';
import { EmployeeTrainingStatus } from '@/types/employee';

// Utils
import { formatMonthYearDisplay } from '@shared/utils';
import { parseDate } from '@/utils/date';
import { processImageUrlForRemotion } from '@/utils/url';
import { calculateTitleFontSize, truncateTitle } from './textLayout';

// Constants
import { MONTHS } from '@shared/constants';
import {
  SCENE5B_DURATION,
  SCENE5B_LAYOUT,
  SCENE5B_LIMITS,
  SCENE5B_LINE_RETRACT_BUFFER,
  SCENE5B_SLIDE_DURATION,
  SCENE5B_REVEAL_END_FRAME,
} from '../constants';

// Format date range, e.g. "Feb - Mar, 2025"
const formatTrainingDateRange = (
  startDate?: string,
  endDate?: string,
): string | null => {
  const start = parseDate(startDate);
  const end = parseDate(endDate);

  if (!start || !end) return null;

  const startYear = start.getUTCFullYear();
  const endYear = end.getUTCFullYear();

  if (startYear === endYear) {
    return `${MONTHS[start.getUTCMonth()]} - ${MONTHS[end.getUTCMonth()]}, ${startYear}`;
  }

  return `${formatMonthYearDisplay(start)} - ${formatMonthYearDisplay(end)}`;
};

const getLatestCompletedTrainings = (
  trainings: EmployeeTraining[],
): EmployeeTraining[] =>
  trainings
    .filter((t) => t.status === EmployeeTrainingStatus.COMPLETED)
    .sort((a, b) => {
      const timeA =
        parseDate(a.actualEnd)?.getTime() ?? parseDate(a.start)?.getTime() ?? 0;
      const timeB =
        parseDate(b.actualEnd)?.getTime() ?? parseDate(b.start)?.getTime() ?? 0;
      return timeB - timeA || b.id.localeCompare(a.id);
    })
    .slice(0, SCENE5B_LIMITS.maxLatestTrainings);

// Calculate horizontal offsets for cards based on count (1-3 cards)
const getGroupOffsets = (count: number): number[] => {
  const { centerOffsetX, pitchX } = SCENE5B_LAYOUT.group;
  if (count <= 1) return [centerOffsetX];

  if (count === 2) {
    return [centerOffsetX - pitchX / 2, centerOffsetX + pitchX / 2];
  }

  return [centerOffsetX - pitchX, centerOffsetX, centerOffsetX + pitchX];
};

// Map a training to card display data
const mapTrainingToCardData = (
  training: EmployeeTraining,
  targetOffsetX: number,
  uniformFontSize: number,
): Scene5bCardData => {
  const rawTitle = training.topicLabel?.trim() || 'Training';
  const supporters =
    training.supporters?.slice(0, SCENE5B_LIMITS.maxAvatarsPerCard) ?? [];

  return {
    id: training.id,
    targetOffsetX,
    titleText: truncateTitle(rawTitle, uniformFontSize),
    titleFontSize: uniformFontSize,
    dateLabel: formatTrainingDateRange(training.start, training.actualEnd),
    avatars: supporters
      .map((s) => ({
        src: processImageUrlForRemotion(s.avatar, staticFile) ?? '',
        name: s.name,
      }))
      .filter((a) => a.src !== ''),
  };
};

// Calculate card group duration (animation + reading time)
const getGroupDuration = (cardCount: number) =>
  Math.max(
    SCENE5B_DURATION.minGroupDuration,
    cardCount > 0
      ? SCENE5B_REVEAL_END_FRAME +
          SCENE5B_DURATION.baseReadFrames +
          cardCount * SCENE5B_DURATION.perCardReadFrames
      : 0,
  );

// Build timed card groups from trainings
export const createCardGroupsWithTiming = (
  trainings: EmployeeTraining[] = [],
): Scene5bCardGroupWithTiming[] => {
  const completed = getLatestCompletedTrainings(trainings);
  if (!completed.length) return [];

  // Single font size for all cards across all groups
  const titles = completed.map((t) => t.topicLabel?.trim() || 'Training');
  const uniformFontSize = calculateTitleFontSize(titles);

  const { maxCardsPerGroup } = SCENE5B_LIMITS;
  let cumulativeFrame = 0;
  const result: Scene5bCardGroupWithTiming[] = [];

  for (let i = 0; i < completed.length; i += maxCardsPerGroup) {
    const chunk = completed.slice(i, i + maxCardsPerGroup);
    const offsets = getGroupOffsets(chunk.length);

    const cards: Scene5bCardGroup = chunk.map((training, idx) =>
      mapTrainingToCardData(
        training,
        offsets[idx] ?? SCENE5B_LAYOUT.group.centerOffsetX,
        uniformFontSize,
      ),
    );

    const isLast = i + maxCardsPerGroup >= completed.length;
    const baseDuration = getGroupDuration(cards.length);

    // Add slide-out frames to the last group to exit to the left
    const durationInFrames = isLast
      ? baseDuration + SCENE5B_SLIDE_DURATION
      : baseDuration;
    result.push({ cards, startFrame: cumulativeFrame, durationInFrames });

    // Overlap: next group starts slideDuration before this one ends
    cumulativeFrame += durationInFrames - (isLast ? 0 : SCENE5B_SLIDE_DURATION);
  }

  return result;
};

// Calculate total scene duration across all card groups
export const calculateScene5bDuration = (
  trainings: EmployeeTraining[] = [],
): number => {
  const cardGroups = createCardGroupsWithTiming(trainings);
  const lastGroup = cardGroups.at(-1);

  if (!lastGroup) return 0;

  return Math.max(
    SCENE5B_DURATION.minSceneDuration,
    lastGroup.startFrame +
      lastGroup.durationInFrames +
      SCENE5B_LINE_RETRACT_BUFFER,
  );
};
